import React, { useState, useEffect } from 'react';
import { FiX, FiUsers, FiEdit3, FiTrash2, FiCopy } from 'react-icons/fi';
import BatchEditPropertiesDialog from './BatchEditPropertiesDialog';
import SchuelerModal from './SchuelerModal';
import CopyMoveDialog from './CopyMoveDialog';
import { usePlanName } from '../PlanNameContext';
import { useConfirmAlert } from '../hooks/useConfirmAlert';

const BatchOperationsModal = ({ 
  show, 
  onClose, 
  selectedTasks = [],
  onConfirm,
  allTasks = [],
  allPanels = []
}) => {
  const [selectedOperation, setSelectedOperation] = useState('');
  const [showEditPropertiesDialog, setShowEditPropertiesDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSchuelerModal, setShowSchuelerModal] = useState(false);
  const [showCopyMoveDialog, setShowCopyMoveDialog] = useState(false);

  const { currentPlanName } = usePlanName();
  const { alert } = useConfirmAlert();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedOperation('');
      setShowEditPropertiesDialog(false);
      setShowDeleteConfirm(false);
      setShowSchuelerModal(false);
      setShowCopyMoveDialog(false);
    }
  }, [show]);

  if (!show) return null;

  // Hilfsfunktion um bereits zugewiesene Lernende aus allen ausgewählten Tasks zu sammeln
  const getExistingStudentsFromTasks = (tasks) => {
    const allStudents = new Map(); // Map um Duplikate zu vermeiden
    
    tasks.forEach(task => {
      if (task.assignedStudents && Array.isArray(task.assignedStudents)) {
        task.assignedStudents.forEach(student => {
          if (student.id) {
            allStudents.set(student.id, student);
          }
        });
      }
    });
    
    return Array.from(allStudents.values());
  };

  // Hilfsfunktion um gemeinsame Task-Daten zu extrahieren (z.B. Klassensynchronisation)
  // eslint-disable-next-line no-unused-vars
  const getCommonTaskData = (tasks) => {
    if (tasks.length === 0) return null;
    
    // Sammle alle Klassensynchronisation-Werte
    const klassensynchronisationValues = tasks
      .map(task => task.klassensynchronisation)
      .filter(value => value && value.trim().length > 0);
    
    // Finde den häufigsten Wert oder kombiniere alle eindeutigen Werte
    const uniqueKlassen = new Set();
    klassensynchronisationValues.forEach(value => {
      if (value) {
        value.split(',').forEach(klasse => {
          const trimmedKlasse = klasse.trim();
          if (trimmedKlasse) {
            uniqueKlassen.add(trimmedKlasse);
          }
        });
      }
    });
    
    const combinedKlassensynchronisation = Array.from(uniqueKlassen).sort().join(',');
    
    return {
      klassensynchronisation: combinedKlassensynchronisation
    };
  };

  const handleDeleteConfirm = () => {
    onConfirm({
      operation: 'delete',
      taskIds: selectedTasks.map(task => task.id)
    });
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };



  return (
    <div className="modal-overlay">
      <div className="modal-content batch-modal">
        <div className="modal-header">
          <h3>
            Batch-Operationen
            <span className="task-count">
              ({selectedTasks.length} Task{selectedTasks.length !== 1 ? 's' : ''} ausgewählt)
            </span>
          </h3>
          <button 
            className="close-button"
            onClick={onClose}
            title="Schließen"
          >
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          {/* Ausgewählte Tasks Übersicht */}
          <div className="selected-tasks-overview">
            <h4>Ausgewählte Tasks:</h4>
            <div className="tasks-list">
              {selectedTasks.map((task, index) => (
                <div key={task.id || index} className="task-item">
                  <div className="task-preview" style={{
                    backgroundColor: task.backgroundColor || '#007bff',
                    borderColor: task.borderColor || '#0056b3'
                  }}>
                    <span className="task-label">{task.label || 'Unbenannter Task'}</span>
                    <span className="task-dates">
                      {task.startDate && task.endDate ? (
                        `${task.startDate.toLocaleDateString('de-DE')} - ${task.endDate.toLocaleDateString('de-DE')}`
                      ) : 'Keine Daten'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Operations Auswahl */}
          <div className="operations-section">
            <h4>Operation auswählen:</h4>
            <div className="operations-grid">
              <button
                className={`operation-button ${selectedOperation === 'assign-students' ? 'selected' : ''}`}
                onClick={async () => {
                  // Prüfe ob Plan gespeichert werden muss
                  if (currentPlanName === 'unbenannt') {
                    await alert(
                      'Bevor Sie Lernende zuordnen können, muss der Plan gespeichert werden.\n\nBitte speichern Sie Ihren Plan zuerst (Ctrl+S oder "Speichern als").',
                      { title: "Plan speichern erforderlich" }
                    );
                    return;
                  }
                  
                  setSelectedOperation('assign-students');
                  setShowSchuelerModal(true);
                }}
              >
                <FiUsers />
                <span>Lernende zuweisen</span>
              </button>

              <button
                className={`operation-button ${selectedOperation === 'edit-properties' ? 'selected' : ''}`}
                onClick={() => setShowEditPropertiesDialog(true)}
              >
                <FiEdit3 />
                <span>Eigenschaften bearbeiten</span>
              </button>

              <button
                className={`operation-button ${selectedOperation === 'copy-move' ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedOperation('copy-move');
                  setShowCopyMoveDialog(true);
                }}
              >
                <FiCopy />
                <span>Kopieren/Verschieben</span>
              </button>

              <button
                className={`operation-button danger ${selectedOperation === 'delete' ? 'selected' : ''}`}
                onClick={() => setShowDeleteConfirm(true)}
              >
                <FiTrash2 />
                <span>Löschen</span>
              </button>
            </div>
          </div>

        </div>

        <div className="modal-footer">
          <button 
            className="cancel-button"
            onClick={onClose}
          >
            Schließen
          </button>
        </div>
      </div>

      {/* Eigenschaften-Bearbeitungs-Dialog */}
      <BatchEditPropertiesDialog
        show={showEditPropertiesDialog}
        onClose={() => setShowEditPropertiesDialog(false)}
        selectedTasks={selectedTasks}
        onConfirm={(result) => {
          setShowEditPropertiesDialog(false);
          onConfirm(result);
          onClose();
        }}
      />

      {/* Lernende-Zuweisungs-Dialog */}
      <SchuelerModal
        show={showSchuelerModal}
        onClose={() => setShowSchuelerModal(false)}
        onUebernehmen={(selectedLernendeIds, lernendeData, actionMode, klassensynchronisation) => {
          setShowSchuelerModal(false);
          
          // Erstelle das Ergebnis-Objekt für die Batch-Operation
          const result = {
            operation: 'assign-students',
            taskIds: selectedTasks.map(task => task.id),
            settings: {
              selectedLernendeIds,
              lernendeData,
              actionMode, // 'add' oder 'remove'
              klassensynchronisation
            }
          };
          
          onConfirm(result);
          onClose();
        }}
        existingStudents={getExistingStudentsFromTasks(selectedTasks)}
        currentTaskData={null} // Bei Batch-Operationen keine DB-Ladung
      />

      {/* Copy/Move Dialog */}
      <CopyMoveDialog
        show={showCopyMoveDialog}
        onClose={() => setShowCopyMoveDialog(false)}
        selectedTasks={selectedTasks}
        allTasks={allTasks}
        allPanels={allPanels}
        onConfirm={(result) => {
          setShowCopyMoveDialog(false);
          onConfirm(result);
          onClose();
        }}
      />

      {/* Lösch-Bestätigungsdialog */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleDeleteCancel}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              border: '1px solid #ddd',
              minWidth: '400px',
              maxWidth: '500px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#d32f2f',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FiTrash2 />
              Tasks löschen?
            </h4>
            <p style={{ 
              margin: '0 0 20px 0', 
              fontSize: '14px', 
              color: '#666', 
              lineHeight: '1.5' 
            }}>
              Möchten Sie wirklich <strong>{selectedTasks.length} Task{selectedTasks.length !== 1 ? 's' : ''}</strong> permanent löschen?
              <br />
              <br />
              <strong style={{ color: '#d32f2f' }}>Diese Aktion kann nicht rückgängig gemacht werden!</strong>
            </p>
            
            {/* Liste der zu löschenden Tasks */}
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                Zu löschende Tasks:
              </div>
              {selectedTasks.map((task, index) => (
                <div key={task.id || index} style={{
                  padding: '6px 8px',
                  marginBottom: '4px',
                  borderRadius: '4px',
                  backgroundColor: task.backgroundColor || '#007bff',
                  color: 'white',
                  fontSize: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '500' }}>{task.label || 'Unbenannter Task'}</span>
                  <span style={{ opacity: 0.8, fontSize: '10px' }}>{task.panelTitle}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                style={{
                  cursor: 'pointer',
                  color: '#666',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onClick={handleDeleteCancel}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#e0e0e0';
                  e.target.style.borderColor = '#ccc';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = '#f5f5f5';
                  e.target.style.borderColor = '#ddd';
                }}
              >
                Abbrechen
              </button>
              <button
                style={{
                  cursor: 'pointer',
                  color: 'white',
                  backgroundColor: '#d32f2f',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={handleDeleteConfirm}
                onMouseEnter={e => e.target.style.backgroundColor = '#b71c1c'}
                onMouseLeave={e => e.target.style.backgroundColor = '#d32f2f'}
                autoFocus
              >
                <FiTrash2 />
                {selectedTasks.length} Task{selectedTasks.length !== 1 ? 's' : ''} löschen
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .modal-content.batch-modal {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #f8f9fa;
        }

        .modal-header h3 {
          margin: 0;
          color: #333;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .task-count {
          font-size: 14px;
          color: #6c757d;
          font-weight: normal;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #6c757d;
          padding: 5px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          background-color: #e9ecef;
        }

        .modal-body {
          padding: 20px;
        }

        .selected-tasks-overview {
          margin-bottom: 30px;
        }

        .selected-tasks-overview h4 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 16px;
        }

        .tasks-list {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 10px;
        }

        .task-item {
          margin-bottom: 10px;
        }

        .task-item:last-child {
          margin-bottom: 0;
        }

        .task-preview {
          padding: 8px 12px;
          border-radius: 4px;
          border: 1px solid;
          background-color: #007bff;
          color: white;
        }

        .task-label {
          display: block;
          font-weight: 600;
          font-size: 12px;
        }

        .task-dates {
          display: block;
          font-size: 10px;
          opacity: 0.8;
          margin-top: 2px;
        }

        .operations-section {
          margin-bottom: 30px;
        }

        .operations-section h4 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 16px;
        }

        .operations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
        }

        .operation-button {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .operation-button:hover {
          border-color: #007bff;
          background-color: #f8f9fa;
        }

        .operation-button.selected {
          border-color: #007bff;
          background-color: #e7f3ff;
          color: #007bff;
        }

        .operation-button.danger {
          border-color: #dc3545;
          color: #dc3545;
        }

        .operation-button.danger:hover {
          border-color: #dc3545;
          background-color: #f8d7da;
        }

        .operation-button.danger.selected {
          border-color: #dc3545;
          background-color: #f8d7da;
          color: #dc3545;
        }

        .operation-details {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
          background-color: #f8f9fa;
        }

        .operation-details h4 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 16px;
        }

        .operation-settings {
          color: #6c757d;
        }

        .settings-description {
          margin: 0 0 15px 0;
          line-height: 1.5;
        }

        .warning-message {
          background-color: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
        }

        .modal-footer {
          padding: 15px 20px;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          background-color: #f8f9fa;
        }

        .cancel-button {
          padding: 8px 16px;
          border: 1px solid #6c757d;
          background: white;
          color: #6c757d;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .cancel-button:hover {
          background-color: #6c757d;
          color: white;
        }

        .confirm-button {
          padding: 8px 16px;
          border: 1px solid #007bff;
          background: #007bff;
          color: white;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .confirm-button:hover {
          background-color: #0056b3;
        }

        .confirm-button:disabled {
          background-color: #6c757d;
          border-color: #6c757d;
          cursor: not-allowed;
        }

        .confirm-button.danger {
          background-color: #dc3545;
          border-color: #dc3545;
        }

        .confirm-button.danger:hover {
          background-color: #c82333;
          border-color: #bd2130;
        }

        .open-properties-dialog-button {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
          margin-top: 10px;
        }

        .open-properties-dialog-button:hover {
          background-color: #0056b3;
        }
      `}</style>
    </div>
  );
};

export default BatchOperationsModal;